<template>
  <el-dialog
    title="充值"
    :visible="dialogVisible"
    width="520px"
    :show-close="false"
    :close-on-click-modal="false"
    :close-on-press-escape="!submitting"
    @close="handleClose"
  >
    <el-form
      :model="form"
      :rules="rules"
      label-position="top"
      class="recharge"
      ref="form"
    >
      <el-form-item label="广告账户 ID">
        <el-input
          :value="normalizedActId"
          disabled
          placeholder="未选择广告账户"
        ></el-input>
      </el-form-item>
      <el-form-item
        label="账单归属"
        prop="billingOwnershipId"
      >
        <el-select
          v-model="form.billingOwnershipId"
          class="formItem"
          filterable
          placeholder="请选择账单归属"
          :loading="ownershipLoading"
          :disabled="!normalizedActId || submitting"
        >
          <el-option
            v-for="item in ownerships"
            :key="item.id"
            :label="item.label"
            :value="item.id"
          ></el-option>
        </el-select>
        <p
          v-if="ownershipHint"
          class="recharge-hint"
        >
          {{ ownershipHint }}
        </p>
      </el-form-item>
      <el-form-item
        label="充值金额"
        prop="rechargeAmount"
      >
        <el-input-number
          v-model="form.rechargeAmount"
          class="formItem"
          :min="0.01"
          :max="5000"
          :precision="2"
          :step="100"
          controls-position="right"
          placeholder="请输入充值金额"
          :disabled="submitting"
        ></el-input-number>
        <p class="recharge-hint">单笔不超过 5000，更大金额请到新资产管理申请</p>
      </el-form-item>
    </el-form>
    <div class="dp-flex justify-end align-item-c recharge-footer">
      <el-button
        type="primary"
        :loading="submitting"
        :disabled="ownershipLoading || !normalizedActId"
        @click="submit"
      >
        确定
      </el-button>
      <el-button
        type="info"
        :disabled="submitting"
        @click="handleClose"
      >
        取消
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import {
  applyFbRecharge,
  getBillOwnershipsForRecharge,
  normalizeActId,
} from '@/api/adAsset/recharge';

const AMOUNT_MAX = 5000;

export default {
  props: {
    dialogVisible: Boolean,
    account_id: [String, Number],
  },
  data() {
    const validateAmount = (rule, value, callback) => {
      const n = Number(value);
      if (value === '' || value === null || value === undefined || Number.isNaN(n)) {
        callback(new Error('请输入充值金额'));
        return;
      }
      if (n < 0.01) {
        callback(new Error('充值金额不能小于 0.01'));
        return;
      }
      if (n > AMOUNT_MAX) {
        callback(new Error('单笔充值不能超过 5000'));
        return;
      }
      callback();
    };
    return {
      submitting: false,
      ownershipLoading: false,
      ownerships: [],
      ownershipHint: '',
      form: {
        billingOwnershipId: undefined,
        rechargeAmount: undefined,
      },
      rules: {
        billingOwnershipId: [{ required: true, message: '请选择账单归属', trigger: 'change' }],
        rechargeAmount: [{ validator: validateAmount, trigger: 'blur' }],
      },
    };
  },
  computed: {
    normalizedActId() {
      return normalizeActId(this.account_id);
    },
  },
  watch: {
    dialogVisible(visible) {
      if (visible) {
        this.resetForm();
        this.$nextTick(() => this.loadBillOwnerships());
      }
    },
  },
  methods: {
    ownershipLabel(item) {
      if (item.name) {
        return item.name;
      }
      return [item.familyName, item.agentName, item.settlementEntityName]
        .filter((part) => part != null && String(part).trim() !== '')
        .join('-');
    },
    toOwnershipId(value) {
      if (value == null || value === '') {
        return undefined;
      }
      const id = Number(value);
      return Number.isNaN(id) ? undefined : id;
    },
    resetForm() {
      this.submitting = false;
      this.ownerships = [];
      this.ownershipHint = '';
      this.form = {
        billingOwnershipId: undefined,
        rechargeAmount: undefined,
      };
      if (this.$refs.form) {
        this.$refs.form.clearValidate();
      }
    },
    handleClose() {
      if (this.submitting) {
        return;
      }
      this.$emit('close');
      this.$emit('update:dialogVisible', false);
    },
    loadBillOwnerships() {
      if (!this.normalizedActId) {
        this.ownershipHint = '未选择广告账户';
        return;
      }
      this.ownershipLoading = true;
      this.ownershipHint = '';
      this.ownerships = [];
      this.form.billingOwnershipId = undefined;
      getBillOwnershipsForRecharge({
        actId: this.normalizedActId,
        platform: 'FACEBOOK',
      })
        .then((res) => {
          const list = Array.isArray(res && res.data) ? res.data : [];
          this.ownerships = list
            .map((item) => {
              const id = this.toOwnershipId(item.id);
              if (id == null) {
                return null;
              }
              return {
                id,
                label: this.ownershipLabel(item) || String(id),
              };
            })
            .filter(Boolean);
          const defaultId = list.length ? this.toOwnershipId(list[0]._defaultId || list[0].DefaultId) : undefined;
          if (defaultId != null && this.ownerships.some((item) => item.id === defaultId)) {
            this.form.billingOwnershipId = defaultId;
          } else if (this.ownerships.length === 1) {
            this.form.billingOwnershipId = this.ownerships[0].id;
          }
          if (!this.ownerships.length) {
            this.ownershipHint = '当前家族没有可充值的账单归属（需已配置 Token）';
          }
        })
        .catch(() => {
          this.ownershipHint = '账单归属加载失败，请稍后重试';
        })
        .finally(() => {
          this.ownershipLoading = false;
        });
    },
    submit() {
      if (!this.normalizedActId) {
        this.$message.warning('未选择广告账户');
        return;
      }
      this.$refs.form.validate((valid) => {
        if (!valid || this.submitting) {
          return;
        }
        this.submitting = true;
        applyFbRecharge({
          actId: this.normalizedActId,
          billingOwnershipId: this.form.billingOwnershipId,
          rechargeAmount: this.form.rechargeAmount,
          currency: 'USD',
        })
          .then(() => {
            this.$message.success('充值申请已提交，请到新资产管理查看结果');
            this.submitting = false;
            this.$emit('close');
            this.$emit('update:dialogVisible', false);
          })
          .catch(() => {
            this.submitting = false;
          });
      });
    },
  },
};
</script>

<style scoped lang="scss">
.recharge {
  .formItem {
    width: 100%;
  }

  ::v-deep .el-input-number {
    width: 100%;

    .el-input__inner {
      text-align: left;
    }
  }
}

.recharge-hint {
  margin: 6px 0 0;
  font-size: 12px;
  line-height: 1.4;
  color: #909399;
}

.recharge-footer {
  margin-top: 8px;

  .el-button + .el-button {
    margin-left: 8px;
  }
}
</style>
